/**
 * AVBP Lead persistence — the Firestore data layer for the front door.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (task #888 — kill the demo sandbox)
 * ─────────────────────────────────────────────────────────────────────────
 *  `lib/leads.ts` is the PURE pipeline (state machine + locked auto-quote +
 *  convert bridge). It has zero Firebase by design so the Node test runner
 *  exercises it directly. Until now the Lead Inbox kept that pure model in
 *  React `useState` (`freshSeedLeads()`), so every refresh lost real work —
 *  the single gate blocking go-live on portal.aayatanveda.in.
 *
 *  This module is the missing thin persistence seam: it maps the pure
 *  `Lead` to/from a `leads/{id}` Firestore document and back, create / list
 *  / update, with NO change to the pure model and NO Firestore-rules
 *  change.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE RULES CONSTRAINT (verified against firebase-functions/firestore.rules
 *  — the LIVE prod file shared with the HR PWA; do NOT loosen it)
 * ─────────────────────────────────────────────────────────────────────────
 *  `leads/{id}` already has a hardened rule block written for the WEBSITE
 *  lead schema, not the rebuild's pure `Lead` shape:
 *
 *   • create  — must carry `name`, `phone`, `source` (∈ a fixed enum),
 *               and `created_via == 'portal_manual'` for the team path.
 *   • update  — NON-admin team is whitelisted to ONLY:
 *               status · priority · assigned_to · notes_internal ·
 *               team_activity · updated_at · updated_by
 *               (admin may additionally touch identity fields).
 *   • delete  — never (audit trail; archive via status).
 *
 *  The rebuild pipeline (state machine, assignedTier, quote, concern,
 *  booking dates, convert ids) does NOT fit that update whitelist as
 *  top-level fields. Loosening the rule was explicitly forbidden (HR
 *  breakage risk — same Firebase project).
 *
 *  SOLUTION (rules-safe, no rule change): the ENTIRE pure `Lead` snapshot
 *  is serialised into ONE structured entry inside `notes_internal` — a key
 *  that IS in the non-admin update whitelist and has no shape constraint in
 *  the rule (the rule whitelists the key, never its contents). The
 *  website-facing top-level fields (`name`, `phone`, `email`, `source`,
 *  `status`, `assigned_to`, …) are mirrored so the existing website /
 *  Brevo / AiSensy tooling and the leads-list rule keep working. The
 *  rebuild reads its model back out of that snapshot.
 *
 *  This is the same "marked insertion seam, zero model change" discipline
 *  as the rest of the front door — `leads.ts` stays pure and untouched.
 *
 *  PURE/IMPURE SPLIT (Sprint-3/4 discipline): `leadToDoc` / `docToLead` /
 *  `pipelineStatus` are PURE and unit-tested directly (no Firebase). Only
 *  the create/list/listen/update wrappers touch Firestore.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  claimConversion,
  conversionClaimIsLive,
  restoreLead,
  trashLead,
} from './leads';
import type { Lead, LeadSource, LeadState } from './leads';
import { joinPhoneKey } from './leadThreadJoin';
import { extractSubmission } from './leadSubmission';
import { normaliseL0Product, type L0Product } from './leadPipeline';

/* ════════════════════ L0 attribution off a flat doc (AD-047) ════════════════════
 *
 * An L0 lead (a website free-magnet submission or the free family Astro-Vastu
 * report) lands as a FLAT doc with NO pipeline snapshot — so its L0 sub-tag +
 * any "consultation paid" flag must be DERIVED from whatever the live funnels
 * write, portal-side, with NO firestore.rules change and without touching the
 * (separately-live) astro backend. Once the portal works the lead, updateLead
 * writes the full snapshot and the snapshot path owns these thereafter. */

/** Derive the L0 product sub-tag from a flat doc (explicit → product → tool code). */
export function l0ProductFromRaw(data: Record<string, unknown>): L0Product | null {
  // 1. explicit field wins (forward-compatible — a writer may set it directly).
  const explicit = normaliseL0Product(data.l0Product);
  if (explicit) return explicit;
  // 2. the astro funnel's product marker (`family_astro_vastu`).
  const product = String(data.product ?? '').toLowerCase();
  if (product.includes('astro')) return 'astro_vastu';
  // 3. the website free-tool source code (E2 = Property Score, E4 = Room-by-Room).
  const src = String(data.source ?? '').toUpperCase();
  if (src === 'E2') return 'E2';
  if (src === 'E4') return 'E4';
  return null;
}

/** Read the optional ₹599 consultation-paid flag off a flat doc (AD-047). */
export function consultationFromRaw(data: Record<string, unknown>): {
  paid: boolean;
  at: string | null;
  amount: number | null;
  currency: 'INR' | 'USD' | null;
} {
  const product = String(data.product ?? '').toLowerCase();
  const status = String(data.payment_status ?? data.paymentStatus ?? '').toLowerCase();
  const explicitPaid =
    data.consultationPaid === true || data.consultation_paid === true;
  // A consultation-product lead that has cleared payment also counts as paid.
  const consultProductPaid =
    product.includes('consult') && (status === 'paid' || data.paid === true);
  const paid = explicitPaid || consultProductPaid;
  const amtRaw = data.consultationAmount ?? data.consultation_amount;
  const amount = typeof amtRaw === 'number' ? amtRaw : paid ? 599 : null;
  const curRaw = String(data.consultationCurrency ?? data.consultation_currency ?? '').toUpperCase();
  const currency = curRaw === 'USD' ? 'USD' : paid ? 'INR' : null;
  const at =
    (typeof data.consultationPaidAt === 'string' && data.consultationPaidAt) ||
    (typeof data.consultation_paid_at === 'string' && data.consultation_paid_at) ||
    null;
  return { paid, at, amount, currency };
}

/* ════════════════════ Source ↔ rules-enum mapping ════════════════════ */

/**
 * The pure model's entry PATHS map onto the rules' fixed `source` enum
 * (rules only allow these values on a team create). No information is
 * lost — the true rebuild source is preserved verbatim inside the pipeline
 * snapshot; this top-level value only has to satisfy the create rule and
 * keep the website tooling coherent.
 *
 *   website         → 'service_enquiry'  (a website enquiry)
 *   free_tool       → 'other'            (lead-magnet result, no exact slot)
 *   manual_referral → 'referral'         (manual / referral entry)
 *   whatsapp        → 'other'            (inbound WhatsApp, no exact slot)
 */
const SOURCE_TO_RULES_ENUM: Record<LeadSource, string> = {
  website: 'service_enquiry',
  free_tool: 'other',
  manual_referral: 'referral',
  whatsapp: 'other',
};

export function rulesSourceFor(source: LeadSource): string {
  return SOURCE_TO_RULES_ENUM[source];
}

/* ══════════ Foreign-channel ingress mapping (the hidden-leads fix) ══════════
 *
 * 2026-05-22 — bug fix. `leads/` carries docs from THREE writers, only one
 * of which (the portal's own `createLead`) embeds the pipeline snapshot:
 *
 *  1. PORTAL front door — `leadToDoc` snapshot in `notes_internal`. Read by
 *     the snapshot path of `docToLead` below.
 *  2. WEBSITE forms — `AV_Website/site` `forms.js` writes a FLAT doc per
 *     `FORMS_SCHEMA.md`: `name`, `email`, `phone`, `source` (a tool code
 *     `E1`/`E2`/`E3`/`E4` or `contact`/`service_enquiry`/`newsletter`),
 *     `source_label`, `status` (`new`/`contacted`/`qualified`/`dropped`),
 *     `responses`, `created_at`. NO snapshot.
 *  3. WHATSAPP inbound — `firebase-functions/avbp_wa_router.js` writes
 *     `{ source:'whatsapp_inbound', status:'triage', phone, firstMessage,
 *     autoCreated:true, waMessageId, createdAt }`. NO snapshot.
 *
 * Pre-fix `docToLead` returned `null` for (2) and (3) → `listenLeads`
 * silently dropped them → those leads were invisible in the portal. The
 * maps below let `docToLead` reconstruct a valid `Lead` from the flat
 * fields so EVERY channel surfaces.
 */

/**
 * Map a raw top-level `source` string (whatever a foreign writer put on the
 * doc) onto the portal `LeadSource` model. Website tool codes
 * (`E1`…`E4`) are lead-magnet results → `free_tool`; the inbound-WhatsApp
 * marker → `whatsapp`; everything else from the website → `website`. The
 * SPECIFIC raw code/label is NOT lost — `docToLead` keeps it in the
 * reconstructed lead's `notes` so display tooling can still show it.
 */
export function leadSourceFromRaw(raw: unknown): LeadSource {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'whatsapp_inbound' || s === 'whatsapp') return 'whatsapp';
  // Website lead-magnet tool codes (E1/E2/E3/E4) → free_tool.
  if (/^e[1-4]$/.test(s)) return 'free_tool';
  if (s === 'free_tool' || s === 'other') return 'free_tool';
  if (s === 'manual_referral' || s === 'referral') return 'manual_referral';
  // contact / service_enquiry / newsletter / anything else → website.
  return 'website';
}

/**
 * Map a foreign-channel `status` string onto the portal `LeadState` model.
 * The website status enum is `new|contacted|qualified|dropped`; the
 * WhatsApp router writes `triage`. A brand-new untouched lead → `enquired`
 * (the first spine state); a triage / contacted lead → `qualifying`; an
 * already-qualified lead → `qualified`; a dropped lead → `rejected`.
 * Unknown / missing → `enquired` (safe default — newest, untouched).
 */
export function leadStateFromRaw(raw: unknown): LeadState {
  const s = String(raw ?? '').trim().toLowerCase();
  switch (s) {
    case 'contacted':
    case 'triage':
      return 'qualifying';
    case 'qualified':
      return 'qualified';
    case 'converted':
      return 'converted';
    case 'dropped':
    case 'rejected':
      return 'rejected';
    case 'archived':
    case 'parked':
      return 'parked';
    default:
      return 'enquired';
  }
}

/* ════════════════════ state ↔ website status mapping ════════════════════ */

/**
 * The website `status` enum is new/contacted/converted/archived. The
 * rebuild's 9-state pipeline maps DOWN onto it so the existing website
 * dashboards stay sane; the precise rebuild state is the source of truth
 * and lives in the snapshot.
 *
 *   enquired                                   → new
 *   qualifying|qualified|consult_booked|        → contacted
 *     discovery_done|quoted
 *   converted                                  → converted
 *   parked|rejected                            → archived
 */
export function pipelineStatus(lead: Lead): string {
  switch (lead.state) {
    case 'enquired':
      return 'new';
    case 'converted':
      return 'converted';
    case 'parked':
    case 'rejected':
      return 'archived';
    default:
      return 'contacted';
  }
}

/* ════════════════════ The Firestore doc shape ════════════════════ */

/** The marker that flags our structured pipeline entry inside notes_internal. */
export const PIPELINE_NOTE_KIND = 'avbp_pipeline_v1';

interface PipelineNote {
  author: string;
  /** JSON.stringify(Lead) — the full pure model snapshot. */
  body: string;
  at: string;
  kind: typeof PIPELINE_NOTE_KIND;
}

export interface LeadDoc {
  /* — website-facing mirror (keeps existing tooling + the leads rule happy) — */
  name: string;
  phone: string;
  email: string;
  source: string; // rules enum value
  source_label: string; // human label = the true rebuild source
  status: string; // website enum (mapped from pipeline state)
  priority: string;
  assigned_to: string | null;
  created_via: 'portal_manual'; // REQUIRED by the team-create rule
  /* — the rebuild pipeline snapshot, rules-whitelisted carrier — */
  notes_internal: PipelineNote[];
  /* — audit (whitelisted, append-only in spirit) — */
  team_activity: Array<{ at: string; by: string; what: string }>;
  updated_at: unknown; // serverTimestamp on write
  updated_by: string;
}

/**
 * PURE — pure `Lead` → the Firestore doc body. Deterministic except the
 * caller-supplied `actorName` (audit author). `updated_at` is filled with
 * a real serverTimestamp by the impure wrappers, NOT here, so this stays
 * unit-testable; tests assert the mirror + the round-trip.
 */
export function leadToDoc(
  lead: Lead,
  actorName: string,
  activityWhat: string,
): Omit<LeadDoc, 'updated_at'> {
  return {
    name: lead.name || 'Unnamed lead',
    phone: lead.phone || '—',
    email: lead.email || '',
    source: rulesSourceFor(lead.source),
    source_label: lead.source,
    status: pipelineStatus(lead),
    priority: 'normal',
    assigned_to: lead.assigneeUid,
    created_via: 'portal_manual',
    notes_internal: [
      {
        author: actorName,
        body: JSON.stringify(lead),
        at: lead.updatedAt,
        kind: PIPELINE_NOTE_KIND,
      },
    ],
    team_activity: [
      { at: lead.updatedAt, by: actorName, what: activityWhat },
    ],
    updated_by: actorName,
  };
}

/**
 * Best-effort ISO string for a Firestore timestamp / Date / string. The
 * website forms + the WA router write a real serverTimestamp; in a live
 * read that arrives as a Firestore `Timestamp` (has `.toDate()`). Tests
 * pass plain strings. Falls back to "" so the caller can decide a default.
 */
function toISO(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  // Firestore Timestamp — has toDate(); also covers a plain Date.
  const maybe = v as { toDate?: () => Date };
  if (typeof maybe.toDate === 'function') {
    try {
      return maybe.toDate().toISOString();
    } catch {
      return '';
    }
  }
  if (v instanceof Date) return v.toISOString();
  // Firestore Timestamp shape {seconds,nanoseconds} (if not the class).
  const ts = v as { seconds?: number };
  if (typeof ts.seconds === 'number') {
    return new Date(ts.seconds * 1000).toISOString();
  }
  return '';
}

/** First non-empty string among the candidates, else "". */
function firstStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/**
 * PURE — reconstruct a `Lead` from a FLAT foreign-channel doc (a website
 * form lead or a WhatsApp-inbound lead — neither carries the pipeline
 * snapshot). Reads only the top-level fields those writers actually emit
 * (`name`/`firstMessage`, `phone`, `email`, `source`, `source_label`,
 * `status`, `created_at`/`createdAt`, `responses.city`/`note_oneline`).
 *
 * This is the hidden-leads fix: pre-2026-05-22 `docToLead` returned `null`
 * for these docs and `listenLeads` dropped them. Now every channel gets a
 * valid `Lead`. The reconstructed lead is read-only in spirit — the portal
 * triage actions still go through the pipeline-snapshot path once a lead is
 * worked (the next `updateLead` re-writes the doc WITH a snapshot).
 */
export function flatDocToLead(
  id: string,
  data: Record<string, unknown>,
): Lead {
  const rawSource = data.source;
  const source = leadSourceFromRaw(rawSource);
  const state = leadStateFromRaw(data.status);
  const responses = (data.responses as Record<string, unknown>) ?? {};

  // Name: website forms carry `name`; the WA router carries no name, only
  // `firstMessage` — fall back to a clear placeholder so the row is never
  // blank / unselectable.
  const name =
    firstStr(data.name) ||
    (source === 'whatsapp' ? 'WhatsApp lead (unknown number)' : 'Unnamed lead');

  const createdAt =
    toISO(data.created_at) || toISO(data.createdAt) || toISO(data.updated_at);
  const updatedAt = toISO(data.updated_at) || createdAt;

  // The raw channel detail (specific tool code / WhatsApp first message)
  // is preserved in `notes` so nothing the foreign writer captured is lost.
  const rawCode = firstStr(rawSource);
  const noteBits: string[] = [];
  if (rawCode) noteBits.push(`Channel: ${rawCode}`);
  const firstMsg = firstStr(data.firstMessage);
  if (firstMsg) noteBits.push(`First message: ${firstMsg}`);
  const existingNote = firstStr(data.note, (responses as { note_oneline?: unknown }).note_oneline);
  if (existingNote) noteBits.push(existingNote);

  return {
    id,
    source,
    state,
    name,
    phone: firstStr(data.phone),
    email: firstStr(data.email),
    location: firstStr(data.location, (responses as { city?: unknown }).city),
    concern: firstStr((responses as { primary_concern?: unknown }).primary_concern),
    propertyType: firstStr((responses as { property_type?: unknown }).property_type),
    sqft: null,
    desiredTier: null,
    assignedTier: null,
    quote: null,
    consultBookedAt: null,
    discoveryDoneAt: null,
    ownerName: firstStr(data.source_label) || LEAD_SOURCE_LABEL_FALLBACK(source),
    assigneeUid:
      typeof data.assigned_to === 'string' ? data.assigned_to : null,
    assigneeName: null,
    notes: noteBits.join(' · '),
    createdAt,
    updatedAt,
    convertedProjectId: null,
    convertedClientNo: null,
    // Contact spine (AD-039) — WhatsApp/website leads are written FLAT (no
    // pipeline snapshot). The WA router stamps `contactId`/`contactLinkSource`
    // flat when the sender matches a known client (admin-SDK write, bypasses
    // rules). Read them off the flat doc so the link surfaces in the portal.
    // Once the portal works the lead, `updateLead` writes the full snapshot
    // and the snapshot path owns these thereafter.
    contactId: firstStr(data.contactId) || null,
    contactLinkSource:
      data.contactLinkSource === 'matched' ||
      data.contactLinkSource === 'manual' ||
      data.contactLinkSource === 'converted'
        ? data.contactLinkSource
        : null,
    // Recoverable Trash — a flat foreign-channel doc has no pipeline
    // snapshot, so trash state would normally live nowhere. Defensively
    // read a top-level `trashed` flag if a foreign writer ever sets one;
    // once the portal trashes a flat lead, `updateLead` writes a full
    // snapshot and the snapshot path below owns the flag thereafter.
    trashed: data.trashed === true,
    trashedAt: firstStr(data.trashedAt) || null,
    trashedBy: firstStr(data.trashedBy) || null,
    // SUBMISSION (2026-06-21) — surface "what the client told us". Extracted
    // off the RAW flat doc (responses/score/observations/flags/areas/
    // artifacts), display-only. `null` for portal-native / WhatsApp channels
    // that carry no website submission payload.
    submission: extractSubmission(data),
    // L0 attribution (AD-047) — derived from the flat funnel doc so an L0 lead
    // attributes under the 5-source lens with its finer sub-tag, and reflects a
    // paid ₹599 consultation. No rules change; the snapshot owns these once the
    // portal works the lead.
    l0Product: l0ProductFromRaw(data),
    ...(() => {
      const c = consultationFromRaw(data);
      return c.paid
        ? {
            consultationPaid: true,
            consultationPaidAt: c.at,
            consultationAmount: c.amount,
            consultationCurrency: c.currency,
          }
        : {};
    })(),
  };
}

/** Tiny label fallback — avoids importing the full LEAD_SOURCE_LABEL map
 *  just for the `ownerName` default on a flat doc. */
function LEAD_SOURCE_LABEL_FALLBACK(source: LeadSource): string {
  return source === 'whatsapp'
    ? 'WhatsApp inbound'
    : source === 'free_tool'
      ? 'Free tool'
      : source === 'manual_referral'
        ? 'Manual / referral'
        : 'Website form';
}

/**
 * PURE — recover the pure `Lead` from a stored doc. Two paths:
 *
 *  1. The doc carries an `avbp_pipeline_v1` snapshot in `notes_internal`
 *     (a lead created / worked through the portal front door) — the
 *     snapshot is authoritative; the doc id overrides the snapshot id
 *     (Firestore is the id authority once persisted).
 *  2. The doc is FLAT — a website-form lead or a WhatsApp-inbound lead.
 *     2026-05-22 fix: instead of returning `null` (which made these leads
 *     invisible), reconstruct a valid `Lead` from the flat top-level
 *     fields via `flatDocToLead`. EVERY channel now surfaces.
 *
 * Returns `null` ONLY when the doc is unusable (a corrupt snapshot AND no
 * usable flat fields) — never silently for a legitimate foreign lead.
 */
export function docToLead(
  id: string,
  data: Record<string, unknown>,
): Lead | null {
  const notes = (data.notes_internal as PipelineNote[] | undefined) ?? [];
  const snap = notes.find((n) => n && n.kind === PIPELINE_NOTE_KIND);
  if (snap && typeof snap.body === 'string') {
    try {
      const lead = JSON.parse(snap.body) as Lead;
      return { ...lead, id };
    } catch {
      // Marked-but-corrupt snapshot — fall through to the flat path so a
      // doc that ALSO has flat fields still surfaces, rather than vanishing.
    }
  }
  // No (readable) snapshot — reconstruct from the flat foreign-channel
  // shape. This is the hidden-leads fix.
  return flatDocToLead(id, data);
}

/* ════════════════════ Firestore wrappers (impure) ════════════════════ */

const leadsCol = () => collection(db, 'leads');

/**
 * Create a fresh lead in `leads/{autoId}`. Returns the Firestore id so the
 * caller can pin the lead to it. Satisfies the team-create rule
 * (name/phone/source-enum/created_via). The Firestore id REPLACES the pure
 * LD-#### id (Firestore is the id authority once persisted) — the caller
 * re-reads via listen and gets the real id back.
 */
export async function createLead(
  lead: Lead,
  actorName: string,
): Promise<string> {
  const body = leadToDoc(lead, actorName, 'Lead created (front door)');
  const ref = await addDoc(leadsCol(), {
    ...body,
    updated_at: serverTimestamp(),
  });
  return ref.id;
}

/**
 * Persist a pipeline mutation (transition / quote / basics / convert) onto
 * an existing lead. ONLY whitelisted keys are written
 * (status/assigned_to/notes_internal/team_activity/updated_*), so a
 * non-admin staff session passes the leads update rule unchanged.
 */
export async function updateLead(
  lead: Lead,
  actorName: string,
  activityWhat: string,
): Promise<void> {
  const body = leadToDoc(lead, actorName, activityWhat);
  await updateDoc(doc(db, 'leads', lead.id), {
    status: body.status,
    assigned_to: body.assigned_to,
    notes_internal: body.notes_internal,
    team_activity: body.team_activity,
    updated_at: serverTimestamp(),
    updated_by: actorName,
  });
}

/* ════════════════════ Conversion claim transaction (Vex gate F2) ════════════════════ */

/** Outcome of a conversion-claim attempt (see claimLeadConversionTx). */
export type ClaimLeadConversion =
  | { kind: 'claimed'; lead: Lead }
  | { kind: 'converted'; lead: Lead }
  | { kind: 'busy' }
  | { kind: 'missing' };

/**
 * ATOMICALLY reserve a lead for conversion — the durable serialisation point
 * of the accept→convert flow. Inside ONE Firestore transaction:
 *   · fresh-read the lead doc (never trust a stale UI snapshot),
 *   · already converted → 'converted' (the caller reuses that engagement),
 *   · a LIVE claim held by a DIFFERENT quote → 'busy' (the concurrent run
 *     wins; nothing is written),
 *   · otherwise stamp `convertClaimId = claimId` via the SAME whitelisted
 *     field set `updateLead` writes (rules-conformant, snapshot-carried).
 * Re-claiming with the SAME claimId (a retry of the same quote) or taking
 * over a STALE claim both return 'claimed' — the caller MUST then look for a
 * project a dead run may already have minted (findConvertedProjectForLead)
 * before minting anything.
 */
export async function claimLeadConversionTx(
  leadId: string,
  claimId: string,
  actorName: string,
): Promise<ClaimLeadConversion> {
  return runTransaction(db, async (tx) => {
    const ref = doc(db, 'leads', leadId);
    const snap = await tx.get(ref);
    if (!snap.exists()) return { kind: 'missing' } as const;
    const lead = docToLead(snap.id, snap.data() as Record<string, unknown>);
    if (!lead || lead.trashed === true) return { kind: 'missing' } as const;
    if (lead.convertedProjectId || lead.state === 'converted') {
      return { kind: 'converted', lead } as const;
    }
    const nowISO = new Date().toISOString();
    if (conversionClaimIsLive(lead, nowISO) && lead.convertClaimId !== claimId) {
      return { kind: 'busy' } as const;
    }
    const claimed = claimConversion(lead, claimId, nowISO);
    const body = leadToDoc(claimed, actorName, `Conversion claimed (quote ${claimId})`);
    tx.update(ref, {
      status: body.status,
      assigned_to: body.assigned_to,
      notes_internal: body.notes_internal,
      team_activity: body.team_activity,
      updated_at: serverTimestamp(),
      updated_by: actorName,
    });
    return { kind: 'claimed', lead: claimed } as const;
  });
}

/**
 * ADMIN-ONLY identity edit (P3 — Khilendra is `staff`, so this is gated in
 * the UI to role==='admin'). The leads rule whitelists name/email/phone/
 * company for ADMIN updates only; a staff session writing these is DENIED.
 * The pure `Lead` snapshot in notes_internal is refreshed in the SAME write
 * so the rebuild reads the corrected identity back (single source of
 * truth). Only the admin-whitelisted identity keys + the always-allowed
 * snapshot/audit keys are touched — never the CF-only callback_* fields.
 *
 * `editLeadIdentity` is PURE (returns the corrected Lead) so it is
 * unit-testable; the updateDoc wrapper is the thin impure seam.
 */
export interface LeadIdentityPatch {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
}

export function editLeadIdentity(
  lead: Lead,
  patch: LeadIdentityPatch,
  nowISO: string,
): Lead {
  return {
    ...lead,
    name: patch.name ?? lead.name,
    phone: patch.phone ?? lead.phone,
    email: patch.email ?? lead.email,
    updatedAt: nowISO,
  };
}

export async function updateLeadAdmin(
  lead: Lead,
  patch: LeadIdentityPatch,
  actorName: string,
  nowISO: string,
): Promise<Lead> {
  let next = editLeadIdentity(lead, patch, nowISO);
  // Vex gate F4 — an identity correction that CHANGES the phone join key
  // invalidates the sticky WhatsApp-thread link (it was matched on the OLD
  // number). Clear it so the identity join re-computes against the corrected
  // number instead of pinning the wrong thread forever.
  if (
    typeof patch.phone === 'string' &&
    joinPhoneKey(patch.phone) !== joinPhoneKey(lead.phone)
  ) {
    next = { ...next, waThreadKey: null, threadLinkSource: null };
  }
  const body = leadToDoc(next, actorName, 'Lead identity corrected (admin)');
  await updateDoc(doc(db, 'leads', lead.id), {
    // admin-whitelisted identity mirror (rules: name/email/phone/company)
    name: body.name,
    email: body.email,
    phone: body.phone,
    ...(typeof patch.company === 'string' ? { company: patch.company } : {}),
    // always-allowed: refreshed snapshot + status mirror + audit
    status: body.status,
    notes_internal: body.notes_internal,
    team_activity: body.team_activity,
    updated_at: serverTimestamp(),
    updated_by: actorName,
  });
  return next;
}

/**
 * Options for the lead listeners.
 *
 * RECOVERABLE TRASH (2026-05-22) — `includeTrashed` controls whether
 * trashed leads are surfaced. The DEFAULT is `false`: the Lead Inbox and
 * every normal count / picker / search must NOT show a trashed lead. The
 * Trash view passes `includeTrashed: true` AND filters to only the trashed
 * rows (see `trashedLeads` below).
 */
export interface LeadListenOpts {
  onErr?: (e: Error) => void;
  /** true → trashed leads are included in the stream (Trash view only). */
  includeTrashed?: boolean;
}

/**
 * Live realtime list of leads in `leads/`, whatever channel wrote it —
 * portal front door (pipeline snapshot), website forms (flat), and inbound
 * WhatsApp (flat). `docToLead` reconstructs flat foreign-channel docs so no
 * lead is dropped. Newest first.
 *
 * RECOVERABLE TRASH — by DEFAULT a trashed lead is filtered OUT here, so it
 * vanishes immediately from the Lead Inbox + every consumer. The 2-arg
 * `(cb, onErr)` form is kept for back-compat; pass a `LeadListenOpts` with
 * `includeTrashed: true` for the Trash view.
 */
export function listenLeads(
  cb: (leads: Lead[]) => void,
  optsOrErr?: LeadListenOpts | ((e: Error) => void),
): Unsubscribe {
  const opts: LeadListenOpts =
    typeof optsOrErr === 'function' ? { onErr: optsOrErr } : optsOrErr ?? {};
  return onSnapshot(
    query(leadsCol()),
    (snap) => {
      const leads: Lead[] = [];
      for (const d of snap.docs) {
        const lead = docToLead(d.id, d.data());
        if (!lead) continue;
        // Trash filter — a trashed lead is hidden unless explicitly wanted.
        if (lead.trashed === true && !opts.includeTrashed) continue;
        leads.push(lead);
      }
      leads.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      cb(leads);
    },
    (e) => opts.onErr?.(e),
  );
}

/**
 * Live realtime list of ONLY the trashed leads — drives the Trash view's
 * leads section. Newest-trashed first (falls back to createdAt).
 */
export function listenTrashedLeads(
  cb: (leads: Lead[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(leadsCol()),
    (snap) => {
      const leads: Lead[] = [];
      for (const d of snap.docs) {
        const lead = docToLead(d.id, d.data());
        if (lead && lead.trashed === true) leads.push(lead);
      }
      leads.sort((a, b) => {
        const ka = a.trashedAt || a.createdAt;
        const kb = b.trashedAt || b.createdAt;
        return ka < kb ? 1 : -1;
      });
      cb(leads);
    },
    (e) => onErr?.(e),
  );
}

/**
 * One-shot read of a single `leads/{id}` doc → pure `Lead` (snapshot or flat).
 * Returns null when the doc is missing/unusable. Used by the Contact-spine
 * merge re-point (AD-039) and any non-reactive caller that has a lead id.
 */
export async function getLead(id: string): Promise<Lead | null> {
  const snap = await getDoc(doc(db, 'leads', id));
  if (!snap.exists()) return null;
  return docToLead(snap.id, snap.data());
}

/** One-shot fetch (used by tests / non-reactive callers). Excludes trashed
 *  leads by default (pass `{ includeTrashed: true }` for all). */
export async function fetchLeads(
  opts: { includeTrashed?: boolean } = {},
): Promise<Lead[]> {
  const snap = await getDocs(query(leadsCol()));
  const leads: Lead[] = [];
  for (const d of snap.docs) {
    const lead = docToLead(d.id, d.data());
    if (!lead) continue;
    if (lead.trashed === true && !opts.includeTrashed) continue;
    leads.push(lead);
  }
  leads.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  return leads;
}

/* ════════════════════ Recoverable Trash — leads (impure) ════════════════════
 *
 * 2026-05-22 — owner decision: a removed lead goes to Trash, never a hard
 * delete. `trashLead` / `restoreLead` (pure, leads.ts) compute the new
 * `Lead`; these wrappers persist it through the SAME rules-whitelisted
 * `updateLead` path — the `trashed` flag rides inside the `notes_internal`
 * pipeline snapshot, which IS in the leads update whitelist, so NO
 * firestore.rules change is needed. A staffer's normal park/reject is
 * unchanged; trash is a separate capability-gated action.
 */

/** Send a lead to the recoverable Trash (persists via the whitelisted path). */
export async function trashLeadDoc(
  lead: Lead,
  actorName: string,
  nowISO: string,
): Promise<Lead> {
  const next = trashLead(lead, actorName, nowISO);
  await updateLead(next, actorName, 'Lead moved to Trash');
  return next;
}

/** Restore a trashed lead from the Trash (it returns to its prior state). */
export async function restoreLeadDoc(
  lead: Lead,
  actorName: string,
  nowISO: string,
): Promise<Lead> {
  const next = restoreLead(lead, nowISO);
  await updateLead(next, actorName, 'Lead restored from Trash');
  return next;
}
